import { useState } from 'react';
import { fetchApiData, interpolate } from '../../Utils/ApiCalls';
import { DOWNLOAD_USAGE_SUMMARY_GG_ENDPOINT, DOWNLOAD_USAGE_SUMMARY_USL, USAGE_SUMMARY_ENDPOINT } from '../../urls';
import useFormStatus from '../../Hooks/useFormStatus';
import { Alert } from '../Alert';
import { COMPANY_ENUM } from '../../Constants/Constants';
import { Button, Input, Typography } from '@attentive-platform/stem-ui';
import { FormContainer } from '../../styled';

const USAGE = {
  DEFAULT: 1,
  BPT: 2
};

const DownloadUsageSummary = ({ company, isBlueprintSummary, downloadBothUsage }) => {
  const initDates = {
    startDate: '',
    endDate: '',
    minEndDate: '',
    maxEndDate: ''
  };

  const [userEmail, setUserEmail] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [loading, setLoading] = useState({
    [USAGE.DEFAULT]: false,
    [USAGE.BPT]: false
  });
  const [dates, setDates] = useState(initDates);

  const { errorMessage, successMessage, handleApiResult } = useFormStatus();

  const isUsl = company === COMPANY_ENUM.USL;
  const generalUsage = !company;

  const handleSubmit = async (e, mode) => {
    e.preventDefault();

    const data = {
      start_date: dates.startDate,
      end_date: dates.endDate
    };
    if (generalUsage) data.email = userEmail.toLowerCase();

    setLoading(prev => ({ ...prev, [mode]: true }));
    const url = generalUsage
      ? USAGE_SUMMARY_ENDPOINT
      : isUsl
        ? interpolate(DOWNLOAD_USAGE_SUMMARY_USL, [companyId])
        : DOWNLOAD_USAGE_SUMMARY_GG_ENDPOINT;
    const apiData = {
      endpoint: url
    };

    if (isBlueprintSummary) {
      data.is_aerial_report_required = false;
      apiData.params = data;
    }

    if (mode === USAGE.BPT) {
      data.is_beam_report_required = true;
    }

    if (isUsl) {
      apiData.params = data;
      apiData.apiRequest = 'GET';
    } else {
      apiData.data = data;
      apiData.apiRequest = 'POST';
    }
    const res = await fetchApiData(apiData);
    handleApiResult(res);
    setLoading(prev => ({ ...prev, [mode]: false }));
    if (res?.data) {
      isUsl && setCompanyId('');
      setDates(initDates);
      let resData;
      if (!isUsl || generalUsage) {
        resData = res?.data?.url;
      } else {
        resData = res?.data?.download;
      }
      window.open(resData, '_blank');
    }
  };

  const handleDateChange = e => {
    const date = e.target.value;
    // Min End Date should be equal or more than start date
    setDates({
      ...dates,
      startDate: date,
      minEndDate: date
    });
  };

  return (
    <form {...(!downloadBothUsage && { onSubmit: e => handleSubmit(e, USAGE.DEFAULT) })}>
      <Alert errorMessage={errorMessage} successMessage={successMessage} />
      <FormContainer>
        {generalUsage && (
          <div className='mb-3'>
            <Input
              label='User Email'
              type='email'
              value={userEmail}
              onChange={e => setUserEmail(e.target.value)}
              placeholder='Enter user email'
              fullWidth
              required
            />
          </div>
        )}
        {isUsl && (
          <div className='mb-3'>
            <Input
              label='Company ID (uuid)'
              type='text'
              value={companyId}
              onChange={e => setCompanyId(e.target.value)}
              placeholder='Enter company UUID'
              fullWidth
              required
            />
          </div>
        )}
        <div className='flex items-center justify-between gap-2 mb-3'>
          <Typography variant='body8'>
            Start Date
          </Typography>
          <input
            type='date'
            value={dates.startDate}
            onChange={e => handleDateChange(e)}
            min='2023-01-01T00:00'
            max='2040-12-31T23:59'
            className='px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
          />
        </div>
        <div className='flex items-center justify-between gap-2 mb-3'>
          <Typography variant='body8'>
            End Date
          </Typography>
          <input
            type='date'
            value={dates.endDate}
            onChange={e => setDates({ ...dates, endDate: e.target.value })}
            min={dates.minEndDate}
            max='2040-12-31T23:59'
            disabled={dates.startDate === ''}
            className='px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed'
          />
        </div>
        <div className='flex justify-end gap-2'>
          {downloadBothUsage ? (
            <>
              <Button
                color='primary'
                size='medium'
                variant='contained'
                onClick={e => handleSubmit(e, USAGE.DEFAULT)}
                loading={loading[USAGE.DEFAULT]}
                className='text-base'>
                Download aerial usage
              </Button>

              <Button
                color='primary'
                size='medium'
                variant='contained'
                onClick={e => handleSubmit(e, USAGE.BPT)}
                loading={loading[USAGE.BPT]}
                className='text-base'>
                Download construction usage
              </Button>
            </>
          ) : (
            <Button
              color='primary'
              size='medium'
              variant='contained'
              onClick={e => handleSubmit(e, USAGE.DEFAULT)}
              type='submit'
              loading={loading[USAGE.DEFAULT]}
              className='text-base'>
              Download
            </Button>
          )}
        </div>
      </FormContainer>
    </form>
  );
};

export default DownloadUsageSummary;
